import React, { useState, useRef, useMemo } from 'react';
import Header from '../../components/common/header/Header';
import '../../pages/dashboard/Dashboard.scss'; // Import global SCSS
import './Alerts.css'; // Import Alerts sidebar styles
import alertDataRaw from '../../data/alert.json';
import VehicleMap from '../../components/Map/vehicleMap/VehicleMap';
import StatusBar from '../../components/Map/statusBar/StatusBar';
import SearchBar from '../../components/common/searchBar/SearchBar';
import mapPin from '../../assets/svgs/map-pin.icon.svg';

import { getRandomDate } from '../../utils/date.utils';

const Alerts = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedAlertIdx, setExpandedAlertIdx] = useState(null);
    const sidebarRef = useRef(null);

    const statusColors = {
        Moving: '#5DF46C',
        Idle: '#E8F45D',
        Stopped: '#D50000',
        Alerts: '#FF3030',
        Maintenance: '#FF6B00',
        Offline: '#9A9A9A',
        Parked: '#00B2FF',
    };

    // Generate dynamic timestamps for alerts
    const processedAlertData = useMemo(() => ({
        ...alertDataRaw,
        alerts: alertDataRaw.alerts.map(alert => ({
            ...alert,
            timestamp: getRandomDate(5) // within last 5 days
        }))
    }), []);

    // Use alerts from alert.json
    const alertList = processedAlertData.alerts;

    // Compute status counts for unique devices per status
    const statusCountsRaw = alertList.reduce((acc, alert) => {
        const status = alert.status;
        if (!acc[status]) {
            acc[status] = { deviceIds: new Set(), color: statusColors[status] || '#999' };
        }
        acc[status].deviceIds.add(alert.deviceId);
        return acc;
    }, {});

    // Convert deviceIds Set to count
    const statusCounts = {};
    Object.keys(statusCountsRaw).forEach(status => {
        statusCounts[status] = {
            count: statusCountsRaw[status].deviceIds.size,
            color: statusCountsRaw[status].color
        };
    });
    // Add Alerts count (total number of alerts)
    statusCounts.Alerts = { count: alertList.length, color: '#FF3030' };

    // Filter alerts based on search query
    const filteredAlerts = alertList.filter(alert =>
        alert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.severity.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Prepare vehicleList for VehicleMap based on filteredAlerts
    const vehicleListForMap = filteredAlerts.map(alert => {
        // Find all alerts for this deviceId
        const allDeviceAlerts = alertList.filter(a => a.deviceId === alert.deviceId);
        // Find the most recent alert for this device
        let recentAlert = null;
        if (allDeviceAlerts.length > 0) {
            recentAlert = allDeviceAlerts.reduce((latest, a) => {
                return new Date(a.timestamp) > new Date(latest.timestamp) ? a : latest;
            }, allDeviceAlerts[0]);
        }
        return {
            vehicleId: alert.deviceId,
            vehicleBasicInfo: {
                vehicle: alert.name,
                deviceId: alert.deviceId,
                licensePlate: alert.licensePlate,
                status: alert.status,
                location: alert.location,
                lat: alert.lat,
                lng: alert.lng,
            },
            vehicleDetailInfo: {},
            recentAlert
        };
    });

    return (
        <>
            <main className="dashboard">
                <Header />
                <section className="content">
                    <article className="vehicle-map-container" style={{ height: 'calc(100vh - 86px)', position: 'relative' }}>
                        <StatusBar statusCounts={statusCounts} />
                        <VehicleMap
                            vehicleList={vehicleListForMap}
                            onClickMarker={(vehicle) => {
                                if (!vehicle) {
                                    setExpandedAlertIdx(null);
                                    return;
                                }
                                // Find the index in filteredAlerts that matches the clicked marker's deviceId
                                const idx = filteredAlerts.findIndex(a => a.deviceId === vehicle.vehicleId);
                                setExpandedAlertIdx(idx !== -1 ? idx : null);
                            }}
                            activeMarkerVehicleOnMapProp={null}
                            onVehicleCardMoreInfoClick={() => { }}
                            activeAlert={expandedAlertIdx !== null && filteredAlerts[expandedAlertIdx] ? {
                                vehicleId: filteredAlerts[expandedAlertIdx].deviceId,
                                type: filteredAlerts[expandedAlertIdx].title,
                                severity: filteredAlerts[expandedAlertIdx].severity,
                                timestamp: filteredAlerts[expandedAlertIdx].timestamp,
                            } : null}
                        />
                    </article>
                    <aside className="sidebar-container" ref={sidebarRef}>
                        <header className="input-group mb-3">
                            <SearchBar value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                        </header>
                        <footer className="sidebar-list" style={{ maxHeight: 'calc(100vh - 165px)' }}>
                            {/* ALERT SECTION START */}
                            {filteredAlerts.map((alert, idx) => {
                                const isExpanded = expandedAlertIdx === idx;
                                return (
                                    <div
                                        key={`alert-${idx}`}
                                        className={`alert-item${isExpanded ? ' expanded' : ''}`}
                                        onClick={() => setExpandedAlertIdx(isExpanded ? null : idx)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="alert-title">
                                            {alert.title}
                                            <span className={`alert-badge ${alert.severity === 'CRITICAL' ? 'critical' : 'minor'}`}>{alert.severity}</span>
                                        </div>
                                        <div className="alert-vehicle">{alert.name}</div>
                                        <div className="alert-time" style={{ color: '#9B9C9E' }}>{new Date(alert.timestamp).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                                        {isExpanded && (
                                            <div className="alert-details">
                                                <div className="alert-detail-row" style={{ fontSize: '14px', color: '#B0B0B0' }} >Device ID: {alert.deviceId}</div>
                                                <div className="alert-detail-row" style={{ fontSize: '14px', color: '#B0B0B0' }} >License Plate No.: {alert.licensePlate}</div>
                                                <div className="alert-detail-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span className={`status-dot`} style={{ background: statusColors[alert.status] || '#999', width: 12, height: 12, borderRadius: '50%', display: 'inline-block' }}></span>
                                                    <span style={{ color: statusColors[alert.status] || '#fff', fontWeight: 600, fontSize: '12px' }}>{alert.status}</span>
                                                    <span style={{ display: 'flex', alignItems: 'center', marginLeft: 12, fontSize: '12px' }}>
                                                        <img src={mapPin} alt="Location" style={{ width: '12px', marginRight: '5px' }} />
                                                        {alert.location}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {/* ALERT SECTION END */}
                        </footer>
                    </aside>
                </section>
            </main>
        </>
    );
};

export default Alerts;

